/**
 * LoginPage.tsx — MER sign-in.
 */
import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, csrfHeader } from '../../auth/AuthContext';
import { sanitiseRedirect } from '../../auth/ProtectedRoute';

const REASON_MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS:    'Email or password is incorrect.',
  VALIDATION_ERROR:       'Please enter a valid email and password.',
  NETWORK_ERROR:          'Network error — check your connection.',
  EMAIL_ALREADY_REGISTERED: 'That email is already registered.',
};

export function LoginPage() {
  const { login }    = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  // from is now a sanitised path string (ProtectedRoute sets state.from as a string)
  const from         = sanitiseRedirect((location.state as any)?.from);
  const hadAuthError = !!(location.state as any)?.authError;

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);

  // ── Phase-2 P2-D1: Google SSO ─────────────────────────────────────────────
  // FAIL CLOSED. The control stays hidden unless the server explicitly says the
  // capability is available, so a request that fails, times out, or returns
  // anything unexpected leaves no button behind. An affordance for a flow that
  // cannot complete is a promise the product cannot keep — the same reasoning
  // that withheld the reset link while P2-D3 shipped disabled.
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleBusy,    setGoogleBusy]    = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/auth/google/status', { credentials: 'include' });
        const d = await r.json();
        if (!cancelled) setGoogleEnabled(d?.enabled === true);
      } catch {
        if (!cancelled) setGoogleEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleGoogle() {
    setError(null);
    setGoogleBusy(true);
    try {
      // The start route is a normal same-origin state-changing MER action and
      // keeps the ordinary CSRF guard. The browser receives ONLY the
      // authorization URL — never the client secret, the PKCE verifier, the
      // nonce or the correlation value, none of which leave the server.
      const r = await fetch('/api/auth/google', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeader()) },
        credentials: 'include',
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d?.ok && typeof d.authorizationUrl === 'string') {
        window.location.assign(d.authorizationUrl);
        return;
      }
      setGoogleBusy(false);
      setError('Google sign-in is not available right now.');
    } catch {
      setGoogleBusy(false);
      setError('Network error — check your connection.');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) {
      navigate(from, { replace: true });
    } else {
      setError(REASON_MESSAGES[result.reason ?? ''] ?? 'Sign-in failed. Please try again.');
    }
  }

  return (
    <div className="mer-full-page" style={{
      background: '#080F1C',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Courier New', Courier, monospace",
      backgroundImage: `linear-gradient(rgba(26,48,80,0.15) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(26,48,80,0.15) 1px, transparent 1px)`,
      backgroundSize: '40px 40px',
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <svg viewBox="0 0 32 36" width="36" height="42" style={{ display: 'block', margin: '0 auto 12px' }}>
            <path d="M2 2 L30 2 L30 24 Q30 34 16 36 Q2 34 2 24 Z" fill="#0D1F35" stroke="#C9A84C" strokeWidth="1.2"/>
            <polyline points="6,28 11,20 16,14 21,17 27,22" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div style={{ fontSize: 10, letterSpacing: 4, color: '#C9A84C', fontWeight: 700 }}>MER ALGO FOOTPRINTS</div>
        </div>

        <form onSubmit={handleSubmit} style={{
          background: 'rgba(13,31,53,0.7)',
          border: '1px solid #1A3050',
          borderRadius: 4, padding: '32px 28px',
        }}>
          <h2 style={{
            margin: '0 0 28px', fontSize: 14, fontWeight: 700,
            letterSpacing: 2, color: '#E8EDF5',
          }}>SIGN IN</h2>

          {hadAuthError && !error && (
            <div style={{
              marginBottom: 16, padding: '9px 14px',
              background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 3, fontSize: 11, color: '#C9A84C', lineHeight: 1.5,
            }}>
              Session could not be verified. Please sign in again.
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: 20, padding: '10px 14px',
              background: 'rgba(180,60,60,0.15)', border: '1px solid rgba(180,60,60,0.4)',
              borderRadius: 3, fontSize: 11, color: '#E87070', lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 9, letterSpacing: 2, color: '#4A6FA5', marginBottom: 6 }}>
              EMAIL
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              required autoComplete="email"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', fontSize: 12,
                background: '#0A1628', border: '1px solid #1A3050',
                borderRadius: 3, color: '#E8EDF5',
                fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={{ display: 'block', fontSize: 9, letterSpacing: 2, color: '#4A6FA5', marginBottom: 6 }}>
              PASSWORD
            </label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              required autoComplete="current-password"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', fontSize: 12,
                background: '#0A1628', border: '1px solid #1A3050',
                borderRadius: 3, color: '#E8EDF5',
                fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>

          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', padding: '12px',
              fontSize: 11, fontWeight: 700, letterSpacing: 2,
              background: loading ? '#6A5428' : '#C9A84C',
              color: '#080F1C', border: 'none', borderRadius: 3,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {loading ? 'SIGNING IN…' : 'SIGN IN'}
          </button>
        </form>

        {/* Phase-2 P2-D13 IMPL-0. This said "Request access →", which named an
            action MER cannot perform: there is no request-access workflow, and
            none is built here. The link led to a page titled CREATE ACCOUNT
            whose form could only fail, because self-registration is disabled
            by governed policy.

            It now leads to a truthful access-information surface and says so.
            The wording promises nothing the estate cannot do — no request, no
            invitation, no signup — and it is deliberately not a call to
            action, because there is no action to call. */}
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: '#3A5070' }}>
          No account?{' '}
          <Link to="/signup" style={{ color: '#C9A84C', textDecoration: 'none' }}>
            Access to MER →
          </Link>
        </div>
        {/* Phase-2 P2-D3 — RESTORED. This link was deliberately withheld while the
            feature shipped disabled, because an affordance for a flow that answers
            404 is a promise the product cannot keep. The controlled production
            proof passed on 2026-09-16 — request, delivery, confirmation, single-use
            and credential-epoch revocation all verified — so the flow is now
            reachable, which is what completes P2-D3's user-facing delivery. */}
        {/* Phase-2 P2-D1. Rendered only when /api/auth/google/status reports the
            capability available, which requires both the enable flag AND
            complete configuration. While Google is unconfigured — which is the
            state this deliverable ships in — nothing appears here at all. */}
        {googleEnabled && (
          <div style={{ marginTop: 16 }}>
            <div style={{ textAlign: 'center', fontSize: 10, letterSpacing: 2, color: '#1A3050', marginBottom: 12 }}>
              OR
            </div>
            <button
              type="button" onClick={handleGoogle} disabled={googleBusy}
              style={{
                width: '100%', padding: '10px 20px', fontSize: 11, letterSpacing: 2,
                background: 'transparent', color: googleBusy ? '#3A5070' : '#E8EDF5',
                border: '1px solid #1A3050', borderRadius: 3, fontFamily: 'inherit',
                cursor: googleBusy ? 'default' : 'pointer',
              }}
            >
              {googleBusy ? 'REDIRECTING…' : 'CONTINUE WITH GOOGLE'}
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: '#3A5070' }}>
          <Link to="/forgot-password" style={{ color: '#3A5070', textDecoration: 'none' }}>
            Forgot your password?
          </Link>
        </div>
        <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: '#3A5070' }}>
          <Link to="/" style={{ color: '#3A5070', textDecoration: 'none' }}>← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
